import { toggleComment, toggleLineComment } from '@codemirror/commands';
import { LanguageSupport, StreamLanguage } from '@codemirror/language';
import type { KeyBinding } from '@codemirror/view';

// Token type names correspond to @codemirror/language's default StreamLanguage
// token table, which maps them to the matching @lezer/highlight tags.
// Mapping used here:
//   keyword   → tags.keyword
//   attribute → tags.attributeName
//   atom      → tags.atom
//   string    → tags.string
//   comment   → tags.comment
//   number    → tags.number
//   link      → tags.link
//   bracket   → tags.bracket
//   variable  → tags.variableName

interface NowlineState {
    inBlockComment: boolean;
    // Set after matching the `nowline` directive keyword so the version
    // token (`v1`, `v2`, …) on the same header line is coloured as a number.
    expectVersion: boolean;
}

const KEYWORDS =
    /^(config|roadmap|include|person|team|anchor|swimlane|item|parallel|group|milestone|footnote|label|style|status|size|scale|calendar|default|description|symbol)$/;

const ATOMS =
    /^(planned|in-progress|done|at-risk|blocked|solid|dashed|dotted|subtle|soft|hard|sans|serif|mono|thin|light|normal|bold|true|false|none|xs|sm|md|lg|xl|full|business|custom|merge|ignore|isolate)$/;

const nowlineStreamParser = StreamLanguage.define<NowlineState>({
    name: 'nowline',

    startState(): NowlineState {
        return { inBlockComment: false, expectVersion: false };
    },

    token(stream, state) {
        if (state.inBlockComment) {
            if (stream.match(/.*?\*\//)) {
                state.inBlockComment = false;
            } else {
                stream.skipToEnd();
            }
            return 'comment';
        }

        // `nowline vN` header: match the directive keyword only at start of
        // line (keyword.control.directive → purple, like VS Code Dark+/Light+).
        if (stream.sol() && stream.match(/nowline(?=[ \t]+v\d+[ \t]*$)/)) {
            state.expectVersion = true;
            return 'keyword';
        }

        if (stream.eatSpace()) return null;

        // Version token following the `nowline` directive
        // (constant.numeric.version → light green, matching VS Code).
        if (state.expectVersion) {
            state.expectVersion = false;
            if (stream.match(/v\d+\b/)) return 'number';
        }

        // Line comment
        if (stream.match('//')) {
            stream.skipToEnd();
            return 'comment';
        }

        // Block comment — may open and close on the same line
        if (stream.match('/*')) {
            if (!stream.match(/.*?\*\//, true)) {
                state.inBlockComment = true;
                stream.skipToEnd();
            }
            return 'comment';
        }

        // Double-quoted strings
        if (stream.eat('"')) {
            while (!stream.eol()) {
                const ch = stream.next();
                if (ch === '"') break;
                if (ch === '\\') stream.next();
            }
            return 'string';
        }

        // List delimiters
        if (stream.eat('[') || stream.eat(']')) return 'bracket';

        // List / property-value separator
        if (stream.eat(',')) return null;

        // Dates (YYYY-MM-DD) — must precede generic numerics
        if (stream.match(/\d{4}-\d{2}-\d{2}\b/)) return 'number';

        // URLs
        if (stream.match(/https?:\/\/[^\s[\],]+/)) return 'link';

        // Durations: 1d 2.5w 3m 1q 2y
        if (stream.match(/\d+(?:\.\d+)?[dwmqy]\b/)) return 'number';

        // Percentages
        if (stream.match(/\d+%/)) return 'number';

        // Hex colour literals
        if (stream.match(/#[0-9a-fA-F]{3,8}\b/)) return 'number';

        // Plain numbers
        if (stream.match(/\d+(?:\.\d+)?/)) return 'number';

        // Identifiers (including hyphenated tokens like `in-progress`, `at-risk`)
        if (stream.match(/[a-zA-Z_][a-zA-Z0-9_-]*/)) {
            const word = stream.current();

            // Property key: word immediately followed by ':'
            if (stream.peek() === ':') return 'attribute';

            if (KEYWORDS.test(word)) return 'keyword';
            if (ATOMS.test(word)) return 'atom';

            return 'variable';
        }

        // Consume any unrecognised character rather than looping forever
        stream.next();
        return null;
    },

    languageData: {
        commentTokens: { line: '//', block: { open: '/*', close: '*/' } },
    },
});

export const nowlineLanguage = new LanguageSupport(nowlineStreamParser);

// Nowline-specific keymap defaults: comment toggling.
// Consumers spread this into their keymap.of([...]) call alongside defaultKeymap.
export const nowlineKeymap: readonly KeyBinding[] = [
    { key: 'Mod-/', run: toggleLineComment },
    { key: 'Mod-Shift-/', run: toggleComment },
];
